import { BsFileText } from 'react-icons/bs';
import { previewDocument, removeUnderscoresAndCapitalize } from '../../utils/miscFunctions';
import { EyeIcon, Download, Upload, Share2 } from 'lucide-react';
import { IDocument } from '../../resources/interfaces';
import useModal from '../../hooks/modalHook';
import PreviewImage from '../elements/PreviewImage';
import Tooltip from '../ToolTip';

type IDocumentDownloadCardProps = {
    title: string;
    documents: any | IDocument[];
    openPictureModal?: (imagURL: string) => void;
    uploadDoc?: () => void;
    sendAgreementFormToCustomer?: () => void;
    isCustomer?: boolean;
}

const DocumentDownloadCard = ({ title, documents, uploadDoc, sendAgreementFormToCustomer, isCustomer = false }: IDocumentDownloadCardProps) => {
    const { ModalComponent, updateModalStates, closeModal } = useModal();

    const isDocumentUrlContainsImageFormat = (url: string) => {
        const imageFormats = ['.jpg', '.jpeg', '.png', '.webp'];
        return imageFormats.some(format => url.toLowerCase().includes(format));
    }

    const openPictureModal = (imgURL: string) => {
        if (!imgURL) return;

        updateModalStates({
            showTitleSection: false,
            children: (
                <PreviewImage onClose={closeModal} imgURL={imgURL} />
            ),
            show: true,
            classes: "!p-0 !rounded-none bg-transparent backdrop-blur-sm",
            onClose: () => updateModalStates({ show: false }),
            size: '3xl',
            hidePanelStyles: true,
            showCloseButton: false,
        });
    };

    return (
        <div className="bg-white rounded-2xl shadow overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
            <div className="flex flex-wrap gap-2 items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-darkBg dark:border-darkBg-border">
                <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
                    <BsFileText className="h-5 w-5 mr-2 text-gray-500 dark:text-darkBg-mainText" />
                    {title ?? 'Documents'}
                </h2>
                {!isCustomer && <button
                    onClick={() => uploadDoc?.()}
                    className="px-1.5 py-0.5 border-2 border-gray-600 dark:border-darkBg-mainText text-gray-700 dark:text-darkBg-mainText hover:bg-gray-50 dark:hover:bg-transparent text-xs font-medium rounded-full transition-all duration-200 flex items-center"
                >
                    <Upload className="h-4 w-4 mr-1.5" />
                    Upload Document
                </button>}
            </div>
            <div className="p-6 max-h-[22rem] overflow-y-auto">
                {Array.isArray(documents) && documents?.length > 0 && (
                    <ul className="divide-y divide-gray-200 dark:divide-darkBg-border">
                        {documents?.map((document: any) => (
                            <li key={document.id} className="py-3 flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <BsFileText className="h-5 w-5 text-gray-400 dark:text-darkBg-mainText" />
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">{removeUnderscoresAndCapitalize(document.docType)}</p>
                                    </div>
                                </div>
                                <div className="flex space-x-2">
                                    {document.docType === 'PAYMENT_AGREEMENT' &&
                                        <button className="text-gray-400 hover:text-gray-500">
                                            <Share2
                                                onClick={sendAgreementFormToCustomer}
                                                className="h-4 w-4"
                                            />
                                        </button>}
                                    <Tooltip message="Preview">
                                        <button
                                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors duration-150 group"
                                            onClick={() => isDocumentUrlContainsImageFormat(String(document.docUrl))
                                                ? openPictureModal?.(document.docUrl)
                                                : previewDocument(document.docUrl)}
                                            title="Preview"
                                        >
                                            <EyeIcon className="h-4 w-4 group-hover:scale-110 transition-transform" />
                                        </button>
                                    </Tooltip>
                                    <Tooltip message="Download">
                                        <button
                                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors duration-150 group"
                                            onClick={() => previewDocument(document.docUrl)}
                                            title="Download"
                                        >
                                            <Download className="h-4 w-4 group-hover:scale-110 transition-transform" />
                                        </button>
                                    </Tooltip>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {!documents && <p className="text-sm text-gray-500">No documents available</p>}
            </div>
            <ModalComponent />
        </div>
    )
}

export default DocumentDownloadCard